import React, { useContext, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch, faXmark } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { connect } from 'react-redux';
import { getErrors, normalizeInchInput, toBnNumber } from '../../../../../common/functions';
import MeasureMentField from '../../../../../Components/MeasureMentField';
import LanguageContext from '../../../../../context/LanguageContext';
import { getFeetToMeterAction } from '../../../../../redux';

const BmiEditModal = ({ bmiId, hasShift, onClose, onSuccess, getFeetToMeter, meter }) => {
  const intl = useIntl();
  const { defaultLanguage } = useContext(LanguageContext);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [studentDetails, setStudentDetails] = useState(null);
  const [date, setDate] = useState('');
  const [feet, setFeet] = useState('');
  const [inch, setInch] = useState('');
  const [weight, setWeight] = useState('');
  const [calculatedMeter, setCalculatedMeter] = useState('');
  const [errors, setErrors] = useState({});
  const [resultMessage, setResultMessage] = useState('');

  useEffect(() => {
    if (!bmiId) return;

    const fetchDetails = async () => {
      setLoading(true);
      try {
        const token = JSON.parse(localStorage.token);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}bmi/participated/student-details-by-id?id=${bmiId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const details = response.data?.data;
        setStudentDetails(details);
        setDate(details?.participation_date || '');
        setFeet(details?.feet ?? '');
        setInch(normalizeInchInput(details?.inch ?? ''));
        setWeight(details?.weight ?? '');
        setCalculatedMeter(details?.height_in_meter ?? '');
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to load BMI details.');
        onClose();
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [bmiId, onClose]);

  useEffect(() => {
    if (meter?.data !== undefined && meter?.data !== null && meter?.data !== '') {
      setCalculatedMeter(meter.data);
    }
  }, [meter?.data]);

  useEffect(() => {
    if (feet !== '' && inch !== '' && studentDetails) {
      getFeetToMeter({ feet, inch });
    }
  }, [feet, inch, getFeetToMeter, studentDetails]);

  const handleCalculate = async () => {
    if (weight < 20) {
      toast.error('Weight can not be less than 20.');
      return;
    }

    if (!studentDetails) return;

    const heightInMeter = calculatedMeter || meter?.data || studentDetails.height_in_meter;

    const payload = {
      institution_id: studentDetails.institution_id,
      student_id: studentDetails.student_id,
      grade: studentDetails.grade,
      has_shift: hasShift?.data?.has_shift,
      shift_id: studentDetails.shift_id,
      section_id: studentDetails.section_id,
      round: studentDetails.participation_round,
      date,
      weight,
      feet,
      inch,
      height_in_meter: heightInMeter,
      academic_year: studentDetails.academic_year,
    };

    setSubmitting(true);
    setErrors({});
    setResultMessage('');

    try {
      const token = JSON.parse(localStorage.token);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}bmi/resubmit/participate`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setResultMessage(response.data?.message || 'BMI Updated Successfully');
      toast.success(response.data?.message || 'BMI Updated Successfully');
      onSuccess();
      onClose();
    } catch (error) {
      const apiErrors = error?.response?.data?.error;
      if (apiErrors && typeof apiErrors === 'object') {
        setErrors(apiErrors);
      }
      toast.error(error?.response?.data?.message || 'Failed to update BMI.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='p-4 fixed top-0 right-0 bottom-0 left-0 w-full h-full grid place-items-center bg-black bg-opacity-75 z-50'>
      <div className='p-4 w-full lg:w-3/4 xl:w-2/3 max-w-4xl bg-white rounded shadow-lg max-h-[90vh] overflow-y-auto'>
        <div className='flex justify-between items-center'>
          <h1 className='text-black font-semibold text-xl capitalize'>
            <FormattedMessage id='edit' /> - {studentDetails?.student_name || ''}
          </h1>
          <button type='button' className='cursor-pointer hover:text-green-600' onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className='my-2 border border-gray-300'></div>

        {loading ? (
          <div className='py-10 text-center'>
            <FontAwesomeIcon icon={faCircleNotch} spin className='text-green-600 text-2xl' />
          </div>
        ) : (
          <div className='space-y-4 py-4'>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='text-[#212529] capitalize text-[12px] lg:text-[14px]'>
                  <FormattedMessage id='roll' />
                </label>
                <input
                  readOnly
                  value={studentDetails?.roll ?? ''}
                  className='block w-full px-3 py-1.5 text-base font-normal text-gray-700 bg-gray-100 border border-solid border-gray-300 rounded'
                />
                <p className='text-xs text-gray-500 mt-1'>
                  <FormattedMessage id='fieldCannotBeChanged' />
                </p>
              </div>
              <div>
                <label className='text-[#212529] capitalize text-[12px] lg:text-[14px]'>
                  <FormattedMessage id='name' />
                </label>
                <input
                  readOnly
                  value={studentDetails?.student_name ?? ''}
                  className='block w-full px-3 py-1.5 text-base font-normal text-gray-700 bg-gray-100 border border-solid border-gray-300 rounded capitalize'
                />
                <p className='text-xs text-gray-500 mt-1'>
                  <FormattedMessage id='fieldCannotBeChanged' />
                </p>
              </div>
              <div>
                <label className='text-[#212529] capitalize text-[12px] lg:text-[14px]'>
                  <FormattedMessage id='gender' />
                </label>
                <input
                  readOnly
                  value={studentDetails?.gender ?? ''}
                  className='block w-full px-3 py-1.5 text-base font-normal text-gray-700 bg-gray-100 border border-solid border-gray-300 rounded capitalize'
                />
                <p className='text-xs text-gray-500 mt-1'>
                  <FormattedMessage id='fieldCannotBeChanged' />
                </p>
              </div>
              <div>
                <label className='text-[#212529] capitalize text-[12px] lg:text-[14px]'>
                  <FormattedMessage id='age' />
                </label>
                <input
                  readOnly
                  value={studentDetails?.age ?? ''}
                  className='block w-full px-3 py-1.5 text-base font-normal text-gray-700 bg-gray-100 border border-solid border-gray-300 rounded'
                />
                <p className='text-xs text-gray-500 mt-1'>
                  <FormattedMessage id='fieldCannotBeChanged' />
                </p>
              </div>
            </div>

            <div className='pb-2'>
              <h2 className='mr-4 inline-block capitalize text-lg text-black font-semibold'>
                <FormattedMessage id='round' />
              </h2>
              <span className='inline-block py-2 px-6 capitalize text-[18px] rounded bg-gradient text-white'>
                <FormattedMessage id='round' />{' '}
                {defaultLanguage === 'en' ? studentDetails?.participation_round : toBnNumber(studentDetails?.participation_round)}
              </span>
            </div>

            <div className='flex items-center pb-2'>
              <h2 className='mr-4 md:w-28 inline-block capitalize text-lg text-black font-semibold'>
                <FormattedMessage id='date' />
              </h2>
              <input
                className='uppercase block w-full min-w-[75px] px-3 py-1.5 text-base font-normal border border-solid border-gray-300 rounded text-gray-700 focus:border-green-600 focus:outline-none bg-white'
                type='date'
                name='date'
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            {errors.date && <small className='text-red-700'>{errors.date}</small>}

            <div className='flex items-center pb-2'>
              <h2 className='mr-4 md:w-28 inline-block capitalize text-lg text-black font-semibold'>
                <FormattedMessage id='height' />
              </h2>
              <div className='ml-3 w-full flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-5'>
                <div className='relative'>
                  <input
                    value={feet}
                    onChange={(e) => setFeet(e.target.value)}
                    placeholder={`${intl.formatMessage({ id: 'ex' })}: ${defaultLanguage === 'en' ? 5 : toBnNumber(5)}`}
                    className='capitalize block w-full px-3 py-1.5 text-base font-normal text-gray-700 bg-white border border-solid border-green-600 rounded focus:outline-none'
                    type='number'
                    name='feet'
                  />
                  <span className='ft_inc_kg absolute top-0 right-0 bg-gray-200 text-black'>
                    <FormattedMessage id='ft' />
                  </span>
                </div>
                <div className='relative'>
                  <input
                    value={inch}
                    onChange={(e) => setInch(normalizeInchInput(e.target.value))}
                    placeholder={`${intl.formatMessage({ id: 'ex' })}: ${defaultLanguage === 'en' ? 5 : toBnNumber(5)}`}
                    className='capitalize block w-full px-3 py-1.5 text-base font-normal text-gray-700 bg-white border border-solid border-green-600 rounded focus:outline-none'
                    type='number'
                    min={0}
                    max={11}
                    step={1}
                    name='inch'
                  />
                  <span className='ft_inc_kg absolute top-0 right-0 bg-gray-200 text-black'>
                    <FormattedMessage id='in' />
                  </span>
                </div>
                <MeasureMentField
                  unit={<FormattedMessage id='meter' />}
                  placeholder={`${calculatedMeter || meter?.data || ''}`}
                  readOnly
                />
              </div>
            </div>
            {(errors.feet || errors.inch || errors.height_in_meter) && (
              <div className='space-y-1'>
                {errors.feet && <small className='text-red-700 block'>{errors.feet}</small>}
                {errors.inch && <small className='text-red-700 block'>{errors.inch}</small>}
                {errors.height_in_meter && <small className='text-red-700 block'>{errors.height_in_meter}</small>}
              </div>
            )}

            <div className='flex items-center pb-2'>
              <h2 className='mr-4 md:w-28 inline-block capitalize text-lg text-black font-semibold'>
                <FormattedMessage id='weight' />
              </h2>
              <div className='relative'>
                <input
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder={`${intl.formatMessage({ id: 'ex' })}: ${defaultLanguage === 'en' ? 50 : toBnNumber(50)}`}
                  className='capitalize block w-full px-3 py-1.5 text-base font-normal text-gray-700 bg-white border border-solid border-green-600 rounded focus:outline-none'
                  type='number'
                  name='weight'
                />
                <span className='ft_inc_kg absolute top-0 right-0 bg-gray-200 text-black'>
                  <FormattedMessage id='kg' />
                </span>
              </div>
            </div>
            {errors.weight && <small className='text-red-700'>{errors.weight}</small>}

            <div className='flex justify-center mt-4'>
              <button
                type='button'
                onClick={handleCalculate}
                disabled={submitting}
                className='w-2/3 sm:w-1/2 py-2 px-6 capitalize text-[14px] lg:text-[18px] rounded text-white bg-gradient hover_gradient disabled:opacity-60'
              >
                {submitting ? (
                  <FontAwesomeIcon icon={faCircleNotch} spin />
                ) : (
                  <FormattedMessage id='calculate' />
                )}
              </button>
            </div>

            {resultMessage && (
              <div className='mt-3 text-center'>
                <h3 className='text-sm lg:text-xl font-semibold text-green-500'>{resultMessage}</h3>
              </div>
            )}

            {Object.keys(errors).length > 0 && !errors.date && !errors.feet && !errors.inch && !errors.weight && !errors.height_in_meter && (
              <div className='text-center'>
                {getErrors(errors).map((msg, index) => (
                  <small key={index} className='text-red-700 block'>
                    {msg.value}
                  </small>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  hasShift: state.login?.currentUser,
  meter: state.meter,
});

const mapDispatchToProps = (dispatch) => ({
  getFeetToMeter: (dataInfo) => dispatch(getFeetToMeterAction(dataInfo)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BmiEditModal);
